import { useMemo } from 'react';
import { FamilyMember } from '@/types';
import { TreeNode, Position } from '@/utils/tree/types';
import { calculateTreeLayout } from '@/utils/tree/layout';
import { TREE_CONFIG } from '@/config';

interface UseTreeLayoutOptions {
  members: FamilyMember[];
  nodeWidth: number;
  nodeHeight: number;
  levelHeight: number;
  frame: { width: number; height: number; padding: number };
}

export function useTreeLayout({
  members,
  nodeWidth,
  nodeHeight,
  levelHeight,
  frame
}: UseTreeLayoutOptions) {
  return useMemo(() => {
    if (!members?.length) {
      return { 
        nodePositions: new Map(),
        contentDimensions: { width: frame.width, height: frame.height }
      };
    }

    // Convert members to tree nodes
    const nodes: TreeNode[] = members.map(member => ({
      id: member.id,
      children: member.children,
      width: nodeWidth,
      height: nodeHeight,
      x: 0,
      y: 0
    }));

    // Calculate initial layout
    const positions = calculateTreeLayout(nodes, nodeWidth, nodeHeight, levelHeight, frame.padding);

    // Calculate content dimensions
    const maxX = Math.max(...Array.from(positions.values()).map(pos => pos.x + nodeWidth));
    const maxY = Math.max(...Array.from(positions.values()).map(pos => pos.y + nodeHeight));

    const contentDimensions = {
      width: Math.max(maxX + frame.padding * 2, frame.width),
      height: Math.max(maxY + frame.padding * 2, frame.height)
    };

    // Center the tree horizontally
    const offset = (contentDimensions.width - maxX) / 2;

    // Apply horizontal centering
    const centeredPositions = new Map<string, Position>();
    positions.forEach((pos, id) => {
      centeredPositions.set(id, {
        x: pos.x + offset,
        y: pos.y + frame.padding
      });
    });

    return { 
      nodePositions: centeredPositions,
      contentDimensions
    };
  }, [members, nodeWidth, nodeHeight, levelHeight, frame]);
}